import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save, Plus, Receipt, X } from 'lucide-react'
import {
  createExpense, getExpenseCategories, getPayees,
  createPayee, getPaymentMethods, uploadMedia, getOwnerSegments
} from '../../services/api'
import useUnsavedChanges from '../../hooks/useUnsavedChanges'
import useSubmitLock from '../../hooks/useSubmitLock'
import { useAuth } from '../../contexts/AuthContext'

export default function ExpenseForm({ ranchId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { userId } = useAuth()

  const [formData, setFormData] = useState({
    ranch_id: ranchId,
    category_id: '',
    description: '',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    payee_id: '',
    payee_name: '', // For custom payee
    receipt_number: '',
    reported_by: userId,
    paid_by_owner_segment_id: '', // single-payer shortcut (105)
    paid_from_float: true, // mig 118: pooled owner contributions paid it
    payment_mode: 'single', // 'single' | 'proportional' | 'split' (106)
    contributions: {}, // owner_id -> amount string, for split mode
  })
  const [receiptPhoto, setReceiptPhoto] = useState(null) // { url, photoId } or null
  const [uploadingReceipt, setUploadingReceipt] = useState(false)
  const [showNewPayeeModal, setShowNewPayeeModal] = useState(false)
  const [error, setError] = useState('')
  const [isDirty, setIsDirty] = useState(false)

  useUnsavedChanges(isDirty)

  // Fetch reference data
  const { data: categories } = useQuery({
    queryKey: ['expenseCategories'],
    queryFn: getExpenseCategories,
  })

  const { data: payees } = useQuery({
    queryKey: ['payees', ranchId],
    queryFn: () => getPayees(ranchId),
  })

  const { data: paymentMethods } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
  })

  // Owner parties for expense attribution (105). Only shown when the ranch has
  // more than one owner, so single-owner ranches see no extra field.
  const { data: ownerSegments } = useQuery({
    queryKey: ['ownerSegments', ranchId, 'all'],
    // includeEmpty: a freshly onboarded ranch has seeded owner segments with
    // no activity yet — the backend still splits expenses across them, so the
    // payer picker must show (submitting with no payer is a 400 by design).
    queryFn: () => getOwnerSegments(ranchId, { includeEmpty: true }),
    enabled: !!ranchId,
  })
  const multiOwner = (ownerSegments?.length || 0) > 1

  // Get selected category for receipt requirement check
  const selectedCategory = categories?.find(c => c.id === formData.category_id)
  const requiresReceipt = selectedCategory?.requires_receipt ?? false
  // 'Other' payee escape-hatch: require the actual payee to be named.
  const selectedPayee = payees?.find(p => p.id === formData.payee_id)
  const isOtherPayee = selectedPayee?.name === 'Other'

  const { locked, lock, unlock } = useSubmitLock()

  // Create expense mutation
  const createMutation = useMutation({
    mutationFn: createExpense,
    onSuccess: (created) => {
      queryClient.invalidateQueries(['expenses'])
      queryClient.invalidateQueries(['financeSummary'])
      navigate('/finance', {
        state: {
          notice: created?.approval_status === 'pending'
            ? 'Expense recorded — awaiting owner/director approval. It will not count in totals until approved.'
            : 'Expense recorded.',
        },
      })
    },
    onError: (err) => setError(err.response?.data?.detail || 'Failed to create expense'),
    onSettled: unlock,
  })

  // Create payee mutation
  const createPayeeMutation = useMutation({
    mutationFn: createPayee,
    onSuccess: (newPayee) => {
      queryClient.invalidateQueries(['payees'])
      setFormData({ ...formData, payee_id: newPayee.id })
      setShowNewPayeeModal(false)
    },
  })

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0]
    if (file) {
      setUploadingReceipt(true)
      try {
        const result = await uploadMedia(file, {
          context: 'receipt',
          ranch_id: ranchId,
        })
        setReceiptPhoto({ url: result.url, photoId: result.id })
      } catch (err) {
        setError(`Failed to upload receipt: ${err.response?.data?.detail || err.message}`)
      } finally {
        setUploadingReceipt(false)
      }
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    // Validation
    if (requiresReceipt && !receiptPhoto && !formData.receipt_number) {
      setError('This category requires a receipt photo or receipt number')
      return
    }

    if (!lock()) return

    // Get payee name if selected from dropdown. For the 'Other' payee, use the
    // specified name the user typed (enforced required in the form).
    let payeeName = formData.payee_name
    if (formData.payee_id) {
      const sp = payees?.find(p => p.id === formData.payee_id)
      payeeName = (sp?.name === 'Other' && formData.payee_name.trim())
        ? formData.payee_name.trim()
        : (sp?.name || '')
    }

    const payload = {
      ranch_id: formData.ranch_id,
      category_id: formData.category_id,
      description: formData.description,
      amount: parseFloat(formData.amount),
      expense_date: formData.expense_date,
      payment_method: formData.payment_method,
      payment_reference: formData.receipt_number || null,
      receipt_urls: receiptPhoto ? [receiptPhoto.url] : [],
      receipt_media_ids: receiptPhoto?.photoId ? [receiptPhoto.photoId] : [],
      reported_by: formData.reported_by,
      payee_name: payeeName,
      payee_msisdn: formData.payee_msisdn || null,
      ...(multiOwner && formData.paid_from_float
        ? { paid_from_float: true }
        : formData.payment_mode === 'proportional'
          ? { paid_proportionally: true }
          : formData.payment_mode === 'split'
            ? {
                contributions: (ownerSegments || [])
                  .map(o => ({ owner_segment_id: o.id, amount: parseFloat(formData.contributions[o.id]) || 0 }))
                  .filter(c => c.amount > 0),
              }
            : { paid_by_owner_segment_id: formData.paid_by_owner_segment_id || null }),
    }

    createMutation.mutate(payload)
  }

  // Group payees by type for better UX
  const groupedPayees = payees?.reduce((acc, payee) => {
    const type = payee.payee_type || 'other'
    if (!acc[type]) acc[type] = []
    acc[type].push(payee)
    return acc
  }, {}) || {}

  const payeeTypeLabels = {
    supplier: 'Suppliers',
    staff: 'Staff',
    contractor: 'Contractors',
    government: 'Government',
    utility: 'Utilities',
    other: 'Other',
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Record Expense</h1>
          <p className="text-gray-600">Track a new farm expense</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} onChange={() => setIsDirty(true)} className="space-y-6">
        {/* Category & Amount */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Expense Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                required
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select category</option>
                {categories?.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name} {cat.requires_receipt && '📄'}
                  </option>
                ))}
              </select>
              {selectedCategory?.requires_receipt && (
                <p className="text-xs text-amber-600 mt-1">
                  📄 This category requires a receipt
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (UGX) *
              </label>
              <input
                type="number"
                required
                min="0"
                step="100"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                required
                value={formData.expense_date}
                onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method *
              </label>
              <select
                required
                value={formData.payment_method}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {paymentMethods?.map(method => (
                  <option key={method.value} value={method.value}>{method.label}</option>
                )) || (
                  <>
                    <option value="cash">Cash</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="bank">Bank Transfer</option>
                  </>
                )}
              </select>
            </div>

            {multiOwner && (
              <div className="rounded-lg border border-gray-200 p-3">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <input type="checkbox" checked={formData.paid_from_float}
                    onChange={(e) => { setFormData({ ...formData, paid_from_float: e.target.checked }); setIsDirty(true) }} />
                  Paid from the farm float (owner contributions)
                </label>
                {formData.paid_from_float && (
                  <p className="text-xs text-gray-500">
                    The cost draws down each owner's float balance by the quarterly
                    herd-share ratio. Untick only if a specific owner paid this
                    personally.
                  </p>
                )}
                {!formData.paid_from_float && (<>
                <label className="block text-sm font-medium text-gray-700 mb-2 mt-3">
                  Who paid for this?
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  The cost is always split across owners by herd share (what each owes). This records who actually <em>paid</em>, for the Director Accounts balance.
                </p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" name="paymode" checked={formData.payment_mode === 'single'}
                      onChange={() => { setFormData({ ...formData, payment_mode: 'single' }); setIsDirty(true) }} />
                    One owner paid it all
                  </label>
                  {formData.payment_mode === 'single' && (
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      required
                      value={formData.paid_by_owner_segment_id}
                      onChange={(e) => { setFormData({ ...formData, paid_by_owner_segment_id: e.target.value }); setIsDirty(true) }}>
                      <option value="">— select owner who paid — (required)</option>
                      {ownerSegments?.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  )}

                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" name="paymode" checked={formData.payment_mode === 'proportional'}
                      onChange={() => { setFormData({ ...formData, payment_mode: 'proportional' }); setIsDirty(true) }} />
                    Paid proportionally by all owners (by herd share)
                  </label>
                  {formData.payment_mode === 'proportional' && (
                    <p className="ml-6 text-xs text-gray-500">Each owner contributes exactly their share — everyone settled, no balances created.</p>
                  )}

                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" name="paymode" checked={formData.payment_mode === 'split'}
                      onChange={() => { setFormData({ ...formData, payment_mode: 'split' }); setIsDirty(true) }} />
                    Custom split — enter each owner&apos;s contribution
                  </label>
                  {formData.payment_mode === 'split' && (
                    <div className="space-y-1">
                      {ownerSegments?.map(o => (
                        <div key={o.id} className="flex items-center gap-2">
                          <span className="w-28 text-sm text-gray-600">{o.name}</span>
                          <input type="number" min="0" placeholder="0"
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                            value={formData.contributions[o.id] || ''}
                            onChange={(e) => { setFormData({ ...formData, contributions: { ...formData.contributions, [o.id]: e.target.value } }); setIsDirty(true) }} />
                        </div>
                      ))}
                      {(() => {
                        const entered = Object.values(formData.contributions).reduce((s, v) => s + (parseFloat(v) || 0), 0)
                        const amt = parseFloat(formData.amount) || 0
                        const ok = Math.abs(entered - amt) < 0.01
                        return (
                          <p className={`text-xs ${ok ? 'text-green-600' : 'text-amber-600'}`}>
                            Entered {entered.toLocaleString()} of {amt.toLocaleString()}
                            {!ok && amt > 0 ? ` — ${(amt - entered).toLocaleString()} remaining` : ''}
                          </p>
                        )
                      })()}
                    </div>
                  )}
                </div>
                </>)}
              </div>
            )}

            {formData.payment_method === 'mobile_money' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payee Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.payee_msisdn || ''}
                  onChange={(e) => setFormData({ ...formData, payee_msisdn: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="256XXXXXXXXX"
                />
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <input
                type="text"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="What was this expense for?"
              />
            </div>
          </div>
        </div>

        {/* Payee */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Payee</h2>
            <button
              type="button"
              onClick={() => setShowNewPayeeModal(true)}
              className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Add New Payee
            </button>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Payee
            </label>
            <select
              value={formData.payee_id}
              onChange={(e) => setFormData({ ...formData, payee_id: e.target.value, payee_name: '' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">-- Select approved payee --</option>
              {Object.entries(groupedPayees).map(([type, typePayees]) => (
                <optgroup key={type} label={payeeTypeLabels[type] || type}>
                  {typePayees.map(payee => (
                    <option key={payee.id} value={payee.id}>{payee.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {(!formData.payee_id || isOtherPayee) && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isOtherPayee ? 'Specify payee *' : 'Or enter payee name'}
              </label>
              <input
                type="text"
                required={isOtherPayee}
                value={formData.payee_name}
                onChange={(e) => setFormData({ ...formData, payee_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter payee name"
              />
            </div>
          )}
        </div>

        {/* Receipt */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Receipt {requiresReceipt && <span className="text-red-500">*</span>}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt Number
              </label>
              <input
                type="text"
                value={formData.receipt_number}
                onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="RCP-001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt Photo
              </label>
              {uploadingReceipt ? (
                <p className="text-sm text-blue-600 p-4">Uploading receipt...</p>
              ) : receiptPhoto ? (
                <div className="relative inline-block">
                  <img src={receiptPhoto.url} alt="Receipt" className="h-24 w-auto rounded-lg border" />
                  <button
                    type="button"
                    onClick={() => setReceiptPhoto(null)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="receipt-upload"
                  />
                  <label
                    htmlFor="receipt-upload"
                    className="flex flex-col items-center cursor-pointer"
                  >
                    <Receipt className="h-8 w-8 text-gray-400 mb-1" />
                    <span className="text-sm text-gray-600">Upload receipt</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          {requiresReceipt && !receiptPhoto && !formData.receipt_number && (
            <p className="text-sm text-amber-600 mt-2">
              ⚠️ Please provide a receipt number or photo for this category
            </p>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={locked || createMutation.isPending || uploadingReceipt}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Save className="h-5 w-5" />
            {createMutation.isPending ? 'Saving...' : uploadingReceipt ? 'Uploading...' : 'Save Expense'}
          </button>
        </div>
      </form>

      {/* New Payee Modal */}
      {showNewPayeeModal && (
        <NewPayeeModal
          ranchId={ranchId}
          onClose={() => setShowNewPayeeModal(false)}
          onSubmit={(data) => createPayeeMutation.mutate(data)}
          isLoading={createPayeeMutation.isPending}
        />
      )}
    </div>
  )
}

function NewPayeeModal({ ranchId, onClose, onSubmit, isLoading }) {
  const [formData, setFormData] = useState({
    ranch_id: ranchId,
    name: '',
    payee_type: 'supplier',
    contact_phone: '',
    contact_email: '',
    notes: '',
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Payee</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payee Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Kampala Vet Supplies"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payee Type *
            </label>
            <select
              required
              value={formData.payee_type}
              onChange={(e) => setFormData({ ...formData, payee_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="supplier">Supplier</option>
              <option value="staff">Staff</option>
              <option value="contractor">Contractor</option>
              <option value="government">Government</option>
              <option value="utility">Utility</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="+256..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {isLoading ? 'Adding...' : 'Add Payee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
